import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardHeader, CardContent, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, FormControlLabel, Switch
} from '@mui/material';
import { Plus, Edit2, Trash2, Network, TestTube, Play, Square, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../api';
import ConfirmModal from '../ConfirmModal';

export default function SocketManagement() {
  const [sockets, setSockets] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSocket, setSelectedSocket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteSocketId, setDeleteSocketId] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    protocol: 'TCP',
    host: '',
    port: 8080,
    messageFormat: 'JSON',
    connectionType: 'Client',
    isActive: true,
    reconnectInterval: 5000,
    bufferSize: 1024,
    timeout: 30000,
    encoding: 'UTF8'
  });

  useEffect(() => {
    loadSockets();
  }, []);

  const loadSockets = async () => {
    setLoading(true);
    try {
      // Mock data - replace with actual API call
      const mockSockets = [
        {
          id: 1,
          name: 'Production Data Socket',
          protocol: 'TCP',
          host: '192.168.1.100',
          port: 8080,
          messageFormat: 'JSON',
          connectionType: 'Client',
          isActive: true,
          connectionStatus: 'connected',
          reconnectInterval: 5000,
          bufferSize: 1024,
          timeout: 30000,
          encoding: 'UTF8',
          messagesReceived: 1247,
          messagesSent: 523,
          lastMessage: new Date().toLocaleString(),
          bytesTransferred: 256789
        },
        {
          id: 2,
          name: 'Alarm Notification Socket',
          protocol: 'UDP',
          host: '192.168.1.200',
          port: 9090,
          messageFormat: 'XML',
          connectionType: 'Server',
          isActive: false,
          connectionStatus: 'disconnected',
          reconnectInterval: 10000,
          bufferSize: 2048,
          timeout: 60000,
          encoding: 'UTF8',
          messagesReceived: 0,
          messagesSent: 0,
          lastMessage: 'Never',
          bytesTransferred: 0
        }
      ];
      setSockets(mockSockets);
    } catch (error) {
      console.error('Error loading sockets:', error);
      toast.error('Socket connections yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (socket = null) => {
    if (socket) {
      setSelectedSocket(socket);
      setFormData({
        name: socket.name,
        protocol: socket.protocol,
        host: socket.host,
        port: socket.port,
        messageFormat: socket.messageFormat,
        connectionType: socket.connectionType,
        isActive: socket.isActive,
        reconnectInterval: socket.reconnectInterval,
        bufferSize: socket.bufferSize,
        timeout: socket.timeout,
        encoding: socket.encoding
      });
    } else {
      setSelectedSocket(null);
      setFormData({
        name: '',
        protocol: 'TCP',
        host: '',
        port: 8080,
        messageFormat: 'JSON',
        connectionType: 'Client',
        isActive: true,
        reconnectInterval: 5000,
        bufferSize: 1024,
        timeout: 30000,
        encoding: 'UTF8'
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSocket(null);
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      // Mock connection test
      await new Promise(resolve => setTimeout(resolve, 2000));
      const success = Math.random() > 0.3; // 70% success rate
      
      if (success) {
        toast.success('Socket connection test successful');
      } else {
        toast.error('Socket connection test failed');
      }
    } catch (error) {
      console.error('Error testing socket connection:', error);
      toast.error('Connection test hatası');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSave = async () => {
    try {
      if (selectedSocket) {
        // Update existing socket
        const updatedSocket = { 
          ...selectedSocket, 
          ...formData,
          lastMessage: new Date().toLocaleString()
        };
        setSockets(sockets.map(s => s.id === selectedSocket.id ? updatedSocket : s));
        toast.success('Socket başarıyla güncellendi');
      } else {
        // Create new socket
        const newSocket = {
          id: Math.max(...sockets.map(s => s.id), 0) + 1,
          ...formData,
          connectionStatus: 'disconnected',
          messagesReceived: 0,
          messagesSent: 0,
          lastMessage: 'Never',
          bytesTransferred: 0
        };
        setSockets([...sockets, newSocket]);
        toast.success('Socket başarıyla eklendi');
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving socket:', error);
      toast.error('Socket kaydedilirken hata oluştu');
    }
  };

  const handleDelete = (socketId) => {
    setDeleteSocketId(socketId);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setSockets(sockets.filter(s => s.id !== deleteSocketId));
      toast.success('Socket başarıyla silindi');
    } catch (error) {
      console.error('Error deleting socket:', error);
      toast.error('Socket silinirken hata oluştu');
    }
  };

  const handleToggleConnection = async (socketId) => {
    try {
      setSockets(sockets.map(s => 
        s.id === socketId 
          ? { 
              ...s, 
              isActive: !s.isActive,
              connectionStatus: !s.isActive ? 'connecting' : 'disconnected'
            }
          : s
      ));
      toast.success('Socket durumu güncellendi');
    } catch (error) {
      console.error('Error toggling socket:', error);
      toast.error('Socket durumu güncellenirken hata oluştu');
    }
  };

  const getStatusChip = (socket) => {
    if (!socket.isActive) {
      return <Chip label="Inactive" size="small" color="default" />;
    }
    
    switch (socket.connectionStatus) {
      case 'connected':
        return <Chip label="Connected" size="small" color="success" icon={<Wifi size={14} />} />;
      case 'connecting':
        return <Chip label="Connecting" size="small" color="warning" />;
      case 'disconnected':
      default:
        return <Chip label="Disconnected" size="small" color="error" icon={<WifiOff size={14} />} />;
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ height: '100%', p: 2 }}>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">Windows Socket Connections</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip 
                  label={`${sockets.length} Sockets`} 
                  size="small" 
                  color="primary" 
                  variant="outlined" 
                />
                <Button 
                  variant="contained" 
                  startIcon={<Plus size={16} />}
                  onClick={() => handleOpenDialog()}
                  disabled={loading}
                >
                  Add Socket
                </Button>
              </Box>
            </Box>
          }
        />
        
        <CardContent sx={{ flex: 1, pt: 1, overflow: 'auto' }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Protocol</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Messages</TableCell>
                  <TableCell>Data Transfer</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sockets.map((socket) => (
                  <TableRow key={socket.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {socket.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {socket.messageFormat}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={socket.protocol} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {socket.host}:{socket.port}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={socket.connectionType} 
                        size="small" 
                        color={socket.connectionType === 'Server' ? 'secondary' : 'primary'}
                      />
                    </TableCell>
                    <TableCell>{getStatusChip(socket)}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          ↓ {socket.messagesReceived} ↑ {socket.messagesSent}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Last: {socket.lastMessage}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatBytes(socket.bytesTransferred)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleConnection(socket.id)}
                          color={socket.isActive ? "error" : "success"}
                          title={socket.isActive ? "Disconnect" : "Connect"}
                        >
                          {socket.isActive ? <Square size={16} /> : <Play size={16} />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(socket)}
                          color="primary"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(socket.id)}
                          color="error"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {sockets.length === 0 && !loading && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: 200, 
              color: 'text.secondary' 
            }}>
              <Network size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
              <Typography variant="body1">No socket connections configured</Typography>
              <Typography variant="body2">Click "Add Socket" to create TCP/UDP connections</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Socket Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedSocket ? 'Edit Socket Connection' : 'Add Socket Connection'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Connection Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Protocol</InputLabel>
                <Select
                  value={formData.protocol}
                  onChange={(e) => setFormData({ ...formData, protocol: e.target.value })}
                  label="Protocol"
                >
                  <MenuItem value="TCP">TCP</MenuItem>
                  <MenuItem value="UDP">UDP</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Connection Type</InputLabel>
                <Select
                  value={formData.connectionType}
                  onChange={(e) => setFormData({ ...formData, connectionType: e.target.value })}
                  label="Connection Type"
                >
                  <MenuItem value="Client">Client</MenuItem>
                  <MenuItem value="Server">Server</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Host/IP Address"
                value={formData.host}
                onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                fullWidth
                required
                placeholder="192.168.1.100 or localhost"
              />
              <TextField
                label="Port"
                type="number"
                value={formData.port}
                onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) || 8080 })}
                fullWidth
                required
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Message Format</InputLabel>
                <Select
                  value={formData.messageFormat}
                  onChange={(e) => setFormData({ ...formData, messageFormat: e.target.value })}
                  label="Message Format"
                >
                  <MenuItem value="JSON">JSON</MenuItem>
                  <MenuItem value="XML">XML</MenuItem>
                  <MenuItem value="Raw">Raw Text</MenuItem>
                  <MenuItem value="Binary">Binary</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Encoding</InputLabel>
                <Select
                  value={formData.encoding}
                  onChange={(e) => setFormData({ ...formData, encoding: e.target.value })}
                  label="Encoding"
                >
                  <MenuItem value="UTF8">UTF-8</MenuItem>
                  <MenuItem value="ASCII">ASCII</MenuItem>
                  <MenuItem value="Unicode">Unicode</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Reconnect Interval (ms)"
                type="number"
                value={formData.reconnectInterval}
                onChange={(e) => setFormData({ ...formData, reconnectInterval: parseInt(e.target.value) || 5000 })}
                fullWidth
              />
              <TextField
                label="Timeout (ms)"
                type="number"
                value={formData.timeout}
                onChange={(e) => setFormData({ ...formData, timeout: parseInt(e.target.value) || 30000 })}
                fullWidth
              />
            </Box>

            <TextField
              label="Buffer Size (bytes)"
              type="number"
              value={formData.bufferSize}
              onChange={(e) => setFormData({ ...formData, bufferSize: parseInt(e.target.value) || 1024 })}
              fullWidth
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<TestTube size={16} />}
                onClick={handleTestConnection}
                disabled={testingConnection || !formData.name || !formData.host}
              >
                {testingConnection ? 'Testing...' : 'Test Connection'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={!formData.name || !formData.host || !formData.port}
          >
            {selectedSocket ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Socket Connection"
        content="Are you sure you want to delete this socket connection? This action cannot be undone."
        type="yesNo"
      />
    </Box>
  );
}